// 高級PDF文本提取服務 - 使用Google Cloud Vision API
import { googleVisionService } from './googleVisionService';

export interface AdvancedPDFResult {
  text: string;
  pageCount: number;
  extractionMethod: 'pdfjs' | 'vision' | 'fallback';
  confidence: number;
  metadata?: {
    title?: string;
    author?: string;
    subject?: string;
    creator?: string;
  };
}

class AdvancedPDFService {
  // 主要PDF文本提取方法
  async extractTextFromFile(file: File): Promise<AdvancedPDFResult> {
    try {
      console.log('開始高級PDF文本提取:', file.name);
      
      // 方法1: 嘗試PDF.js提取（文本型PDF）
      const pdfjsResult = await this.extractWithPDFJS(file);
      if (pdfjsResult.text && pdfjsResult.text.length > 100) {
        console.log('✅ PDF.js提取成功，文本長度:', pdfjsResult.text.length);
        return {
          ...pdfjsResult,
          extractionMethod: 'pdfjs',
          confidence: 0.9
        };
      }
      
      // 方法2: 嘗試Google Vision API OCR（掃描件PDF）
      if (googleVisionService.isConfigured()) {
        try {
          const visionResult = await googleVisionService.extractTextFromPDF(file);
          if (visionResult.text && visionResult.text.length > 50) {
            console.log('✅ Google Vision API提取成功，文本長度:', visionResult.text.length);
            return {
              text: visionResult.text,
              pageCount: visionResult.pageCount,
              extractionMethod: 'vision',
              confidence: visionResult.confidence,
              metadata: {
                title: file.name,
                author: 'Google Vision API',
                subject: 'Insurance Policy OCR'
              }
            };
          }
        } catch (visionError) {
          console.warn('Google Vision API提取失敗:', visionError);
        }
      } else {
        console.warn('Google Vision API未配置，跳過OCR提取');
      }
      
      // 如果都失敗，返回空結果
      console.log('❌ 所有PDF提取方法都失敗');
      return {
        text: '',
        pageCount: 0,
        extractionMethod: 'fallback',
        confidence: 0.0
      };
      
    } catch (error) {
      console.error('高級PDF提取失敗:', error);
      return {
        text: '',
        pageCount: 0,
        extractionMethod: 'fallback',
        confidence: 0.0
      };
    }
  }

  // 方法1: PDF.js提取
  private async extractWithPDFJS(file: File): Promise<{text: string, pageCount: number}> {
    try {
      const pdfjsLib = await import('pdfjs-dist');
      // 使用本地worker文件避免CORS問題
      pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      // 過去這裡寫死只讀前 5 頁，導致保單條款、給付表等通常位於文件中後段的內容
      // 完全沒被擷取到。改成讀取全部頁數，並設一個寬鬆上限避免極端情況
      // （超大量頁數的檔案）卡住瀏覽器分頁太久。
      const MAX_PAGES = 60;
      const pageCount = Math.min(pdf.numPages, MAX_PAGES);
      if (pdf.numPages > MAX_PAGES) {
        console.warn(`⚠️ PDF 共 ${pdf.numPages} 頁，超過 ${MAX_PAGES} 頁上限，僅擷取前 ${MAX_PAGES} 頁`);
      }

      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        
        fullText += pageText + ' ';
      }
      
      return {
        text: this.normalizeText(fullText.trim()),
        pageCount: pdf.numPages
      };
    } catch (error) {
      console.warn('PDF.js提取失敗:', error);
      return { text: '', pageCount: 0 };
    }
  }

  /**
   * 標準化文本格式，確保與pdfPasswordService提取的文本格式一致
   */
  private normalizeText(text: string): string {
    return text
      // 移除多餘的空白字符
      .replace(/\s+/g, ' ')
      // 移除開頭和結尾的空白
      .trim();
  }

}

export const advancedPDFService = new AdvancedPDFService();
